/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-one-expression-per-line */
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import UnderlineLink from '@comp/UnderlineLink/UnderlineLink';
import H1 from '@base/H1/H1';
import H4 from '@base/H4/H4';
import H6 from '@base/H6/H6';
import Thumbnail from '@base/Thumbnail/Thumbnail';
import P1 from '@base/P1/P1';
import P2 from '@base/P2/P2';
import Tags from '@base/Tags/Tags';
import Link from '@base/Link/Link';
import PlayCircleBig from '@icon/PlayCircleBig';
import dateHelper from '@utils/dates';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import analyticsAction from '@actions/analytics';
import useWindowSize from '@hooks/useWindowSize';
import Button from '@base/Button/Button';
import AppInstallBanner from '@comp/AppInstallBanner/Version1';
import { TextColors, Spacings, BgColors } from '@styles/index';
import pushRoute from '@utils/pushRoute';

const SecWrapper = styled.section`
  position: relative;
  margin: auto;
  margin-top: 195px;
  box-shadow: ${({ theme }) => boxShadowGenerator({ theme })};
  border-radius: 8px;
  width: 1136px;
  height: 394px;
  display: flex;
  background-color: ${BgColors.WHITE};
  @media (max-width: 70.95em) {
    margin-top: 88px;
    width: 728px;
  }
  @media (max-width: 47.95em) {
    width: calc(100% - 32px);
    flex-direction: column;
    height: auto;
    padding: 24px 16px;
    margin: 56px 16px 0;
  }
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
  width: 368px;
  margin-left: ${Spacings.SPACING_24B};
  margin-right: ${Spacings.SPACING_24B};
  margin-top: ${Spacings.SPACING_20B};
  margin-bottom: ${Spacings.SPACING_20B};
  @media (max-width: 70.95em) {
    margin: 64px 62px;
    width: 232px;
  }
  @media (max-width: 47.95em) {
    width: 100%;
    margin: 0;
  }
`;

const FreeTags = styled(Tags)`
  background-color: ${BgColors.ORANGE} !important;
  width: max-content;
  margin-bottom: ${Spacings.SPACING_3B};
  font-weight: bold;
  font-size: 16px;
  @media (max-width: 70.95em) {
    font-size: 12px;
    margin-bottom: 8px;
  }
`;

const Info = styled(P1)`
  font-size: 20px;
  margin-top: ${Spacings.SPACING_3B};
  @media (max-width: 70.95em) {
    font-size: 14px;
  }
  @media (max-width: 47.95em) {
    font-size: 16px;
    margin-top: 8px;
  }
`;

const CardsContainer = styled.div`
  position: absolute;
  right: 96px;
  height: 464px;
  top: -35px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  @media (max-width: 70.95em) {
    right: 62px;
    top: -32px;
    & > a:last-child > div {
      margin-bottom: 0;
    }
  }
  @media (max-width: 47.95em) {
    position: relative;
    flex-direction: row;
    justify-content: flex-start;
    height: auto;
    top: 0;
    right: 0;
    left: 0;
    padding: 16px 0 12px;
    overflow-x: scroll;
    overscroll-behavior: unset;
    scroll-behavior: smooth;
    ::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
    & > a > div {
      margin-right: 12px;
    }
  }
`;

const CardWrapper = styled.div`
  width: 416px;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  background: #ffffff;
  box-shadow: ${({ theme }) => boxShadowGenerator({ theme })};
  border-radius: 8px;
  position: relative;
  cursor: pointer;
  margin: 8px 0px;
  @media (max-width: 70.95em) {
    width: 296px;
    margin: 0 0 16px 0;
  }
  @media (max-width: 47.95em) {
    width: 256px;
    margin: 0;
  }
`;

const CardTitle = styled(H6)``;

const CardSubtitle = styled(P2)`
  margin-top: ${Spacings.SPACING_1B};
  color: ${TextColors.SECONDARY};
`;

const CardThumbnail = styled(Thumbnail)`
  width: 128px;
  height: 72px;
  border-radius: 4px;
  @media (max-width: 70.95em) {
    width: 72px;
    min-width: 72px;
  }
`;

const GreenH4 = styled(H4)`
  font-weight: bold;
  font-size: 20px;
  line-height: 150%;
  color: ${TextColors.GREEN};
  @media (max-width: 70.95em) {
    font-size: 14px;
  }
`;

const LinkWrapper = styled.div`
  margin-top: ${Spacings.SPACING_5B};
`;

const PlayButtonContainer = styled.div`
  width: ${Spacings.SPACING_6B};
  height: ${Spacings.SPACING_6B};
  background-color: ${BgColors.WHITE};
  border-radius: 12px;
  z-index: 1;
  position: absolute;
  bottom: 24px;
  left: 24px;
  @media (max-width: 70.95em) {
    display: none;
  }
`;

const LiveTag = styled(Tags)`
  margin-top: ${Spacings.SPACING_1B};
  position: absolute;
  z-index: 1;
  @media (min-width: 70.95em) {
    bottom: 24px;
    left: 96px;
  }
  @media (max-width: 70.95em) {
    left: 42px;
    bottom: 22px;
    padding: 4px 6px;
    margin: 0;
  }
  @media (max-width: 47.95em) {
    left: 48px;
    font-size: 10px;
    padding: 4px 6px;
  }
`;

const Right = styled.div`
  width: 240px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  @media (max-width: 70.95em) {
    width: 176px;
  }
  @media (max-width: 47.95em) {
    width: auto;
    margin-left: 16px;
  }
`;

const RightTop = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled(H1)`
  @media (max-width: 70.95em) {
    font-size: 28px;
    line-height: 120%;
  }
  @media (max-width: 47.95em) {
    font-size: 22px;
  }
`;

const SeeAll = styled(Button)`
  display: none;
  @media (max-width: 47.95em) {
    display: block;
    min-height: 40px;
    height: 40px;
    width: 100%;
    background: #fff;
    border: 1px solid #f0f4f7;
    border-radius: 8px;
    color: #08bd80;
    font-size: 14px;
  }
`;

const AuthorName = styled(P2)``;

const PlayButton = () => {
  return (
    <PlayButtonContainer>
      <PlayCircleBig size="24px" color="rgba(60, 72, 82, 0.48)" />
    </PlayButtonContainer>
  );
};

const getDateText = (d) => {
  const date = new Date(d);
  const timePart = dateHelper.formatTime(date);
  const isToday = dateHelper.isSameDay(new Date(), date);
  let result = '';

  if (isToday) {
    result = `Today, ${timePart}`;
  } else {
    result = `${dateHelper.getFullDate(d)}, ${timePart}`;
  }
  return result;
};

const Card = ({ course, dataAnalytics, dispatch, setOpenAppInstallBanner }) => {
  const { isMobile } = useWindowSize();
  const { uid, thumbnail, name, startsAt, isLive, authors, nextTrack } = course;
  const [author] = authors;

  const href = nextTrack ? '/class/[slug]/[uid]' : '/course/[slug]/[courseUID]';
  const as = nextTrack
    ? `/class/${nextTrack.slug}/${nextTrack.uid}`
    : `/course/-/${uid}`;

  const handleAnalytics = () => {
    // if (isMobile) {
    //   e.preventDefault();
    //   setOpenAppInstallBanner(true);
    // }
    dispatch(
      analyticsAction(dataAnalytics, course.uid, course, {
        'Last primary source': 'goal page',
        'Last Primary Source Section': 'Free Live Classes'
      })
    );
  };
  const handleAuthorClick = (e) => {
    if (isMobile) {
      e.preventDefault();
      setOpenAppInstallBanner(true);
    }
  };
  return (
    <Link href={href} as={as} newTab onClick={handleAnalytics}>
      <CardWrapper>
        {isLive && <PlayButton />}
        {isLive && <LiveTag type="live">LIVE</LiveTag>}
        <CardThumbnail
          imgUrl={thumbnail}
          orientation="vertical"
          data-analytics={dataAnalytics}
        />
        <Right>
          <RightTop>
            <CardTitle lineClamp={1}>{name}</CardTitle>
            <CardSubtitle>{getDateText(startsAt)}</CardSubtitle>
          </RightTop>
          <Link
            href="/[authorID]"
            as={`/@${author?.username}`}
            onClick={handleAuthorClick}
          >
            <AuthorName>{author?.name}</AuthorName>
          </Link>
        </Right>
      </CardWrapper>
    </Link>
  );
};

const PageHead = ({ block, goalUID, goalSlug }) => {
  const { courses } = block;
  const dispatch = useDispatch();
  const [openAppInstallBanner, setOpenAppInstallBanner] = useState(false);
  const { isMobile } = useWindowSize();
  const seeAllAnalytics = () => {
    dispatch(
      analyticsAction(
        'subscription-freeLiveClasses-seeAll',
        '',
        {},
        {
          'Last Primary Source': 'goal page',
          'Last Primary Source Section': 'Free Live Classes'
        }
      )
    );
  };
  const handleSeeAllClick = () => {
    pushRoute(
      '/goal/[goalSlug]/[goalUID]/classes',
      `/goal/${goalSlug}/${goalUID}/classes`
    );
  };

  const closeAppInstallBanner = () => setOpenAppInstallBanner(false);

  return (
    <>
      <SecWrapper>
        <Left>
          <FreeTags type="filled">FREE</FreeTags>
          <Title>Live Classes</Title>
          <Info>Experience Plus for free and start learning from the best</Info>
          {isMobile ? null : (
            <LinkWrapper>
              <UnderlineLink
                href="/goal/[goalSlug]/[goalUID]/classes"
                as={`/goal/${goalSlug}/${goalUID}/classes`}
                color={TextColors.GREEN}
              >
                <GreenH4
                  data-analytics="subscription-seeAll"
                  onClick={seeAllAnalytics}
                >
                  See all
                </GreenH4>
              </UnderlineLink>
            </LinkWrapper>
          )}
        </Left>
        <CardsContainer>
          {courses.map((course) => (
            <Card
              key={course.uid}
              course={course}
              dataAnalytics="subscription-liveClassesCard"
              dispatch={dispatch}
              setOpenAppInstallBanner={setOpenAppInstallBanner}
            />
          ))}
        </CardsContainer>
        <SeeAll label="See all" onClick={handleSeeAllClick} />
      </SecWrapper>
      <AppInstallBanner
        onClose={closeAppInstallBanner}
        open={openAppInstallBanner}
      />
    </>
  );
};

export default PageHead;
